<template>
  <div class="warehouse-page">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" @click="backMainPanel" class="logo-button" />
    </div>

    <div class="page-content">
      <div class="page-header">
        <div>
          <h2>Vales de Salida</h2>
          <p class="page-subtitle">Retiros que restan existencia del almacén · {{ totalCount }} vales</p>
        </div>
        <div class="header-actions">
          <button v-if="canCreate" class="action-button action-primary" @click="openCreateModal">Nuevo vale de salida</button>
          <button class="action-button" @click="backWarehouse">Volver a Almacén</button>
        </div>
      </div>

      <div class="filters-card">
        <div class="filter-group">
          <label for="filter-vs-folio">Folio</label>
          <input id="filter-vs-folio" type="text" v-model="filters.voucher_number" @keyup.enter="applyFilters" placeholder="VS-1" />
        </div>
        <div class="filter-group">
          <label for="filter-vs-part">Pieza</label>
          <input id="filter-vs-part" type="text" v-model="filters.part" @keyup.enter="applyFilters" placeholder="Nombre o código" />
        </div>
        <div class="filter-group">
          <label for="filter-vs-auth">Autorizó</label>
          <input id="filter-vs-auth" type="text" v-model="filters.authorized_by" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group">
          <label for="filter-vs-recv">Recibió</label>
          <input id="filter-vs-recv" type="text" v-model="filters.received_by" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group">
          <label for="filter-vs-order">Orden</label>
          <input id="filter-vs-order" type="text" v-model="filters.order_number" @keyup.enter="applyFilters" placeholder="Folio de orden" />
        </div>
        <div class="filter-group">
          <label for="filter-vs-req">Requisición</label>
          <input id="filter-vs-req" type="text" v-model="filters.requisition_number" @keyup.enter="applyFilters" placeholder="RQ-1" />
        </div>
        <div class="filter-group">
          <label for="filter-vs-from">Desde</label>
          <input id="filter-vs-from" type="date" v-model="filters.start_date" />
        </div>
        <div class="filter-group">
          <label for="filter-vs-to">Hasta</label>
          <input id="filter-vs-to" type="date" v-model="filters.end_date" />
        </div>
        <div class="filter-buttons">
          <button class="action-button action-primary" @click="applyFilters">Buscar</button>
          <button class="action-button" @click="clearFilters">Limpiar</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th :aria-sort="ariaSort('voucher_number')">
                  <button class="th-sort" @click="toggleSort('voucher_number')">
                    <span>Folio</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'voucher_number' }">{{ sortIcon('voucher_number') }}</span>
                  </button>
                </th>
                <th :aria-sort="ariaSort('created_at')">
                  <button class="th-sort" @click="toggleSort('created_at')">
                    <span>Fecha</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'created_at' }">{{ sortIcon('created_at') }}</span>
                  </button>
                </th>
                <th>Autorizó</th>
                <th>Recibió</th>
                <th>Orden vinculada</th>
                <th>Requisición</th>
                <th class="col--num">Piezas</th>
                <th class="col--num">Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in skeletonRows" :key="`skeleton-${n}`">
                  <td v-for="col in 8" :key="col"><span class="skeleton"></span></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="voucher in vouchers" :key="voucher.id">
                  <td><span class="barcode-pill" role="button" tabindex="0" @click="openDetail(voucher)" @keyup.enter="openDetail(voucher)">{{ voucher.voucher_number }}</span></td>
                  <td>{{ formatDateTime(voucher.created_at) }}</td>
                  <td :class="{ 'cell-muted': !voucher.authorized_by_name }">{{ voucher.authorized_by_name || '—' }}</td>
                  <td :class="{ 'cell-muted': !voucher.received_by_name }">{{ voucher.received_by_name || '—' }}</td>
                  <td :class="{ 'cell-muted': !voucher.maintenance_order_number }">{{ voucher.maintenance_order_number || 'Sin vincular' }}</td>
                  <td :class="{ 'cell-muted': !voucher.requisition_number }">{{ voucher.requisition_number || 'Sin requisición' }}</td>
                  <td class="col--num">{{ voucher.items.length }}</td>
                  <td class="col--num cell-strong">${{ formatNumber(voucher.total) }}</td>
                </tr>
                <tr v-if="vouchers.length === 0">
                  <td colspan="8" class="empty-row">
                    <div class="empty-state">
                      <span class="empty-state__icon">📤</span>
                      <span class="empty-state__title">No se encontraron vales de salida</span>
                      <span class="empty-state__hint">Ajusta los filtros o registra un nuevo retiro.</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="pagination-container">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" aria-label="Página anterior">‹</button>
          <span
            v-for="(pageNumber, index) in pageNumbers"
            :key="`${pageNumber}-${index}`"
            :class="{ active: pageNumber === currentPage, ellipsis: pageNumber === ellipsis }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </span>
          <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" aria-label="Página siguiente">›</button>
          <span class="pagination-summary">{{ totalCount }} resultados</span>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <div v-if="detailVoucher" class="modal-overlay" @click.self="detailVoucher = null">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="vs-detail-title">
        <h3 id="vs-detail-title">Vale de salida {{ detailVoucher.voucher_number }}</h3>
        <div class="modal-body">
          <div class="detail-meta">
            <div>
              <span class="detail-meta__label">Fecha</span>
              <span class="detail-meta__value">{{ formatDateTime(detailVoucher.created_at) }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Autorizó</span>
              <span class="detail-meta__value">{{ detailVoucher.authorized_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Recibió</span>
              <span class="detail-meta__value">{{ detailVoucher.received_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Orden vinculada</span>
              <span class="detail-meta__value">{{ detailVoucher.maintenance_order_number || 'Sin vincular' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Requisición</span>
              <span class="detail-meta__value">{{ detailVoucher.requisition_number || 'Sin requisición' }}</span>
            </div>
            <div v-if="detailVoucher.comments" class="detail-meta--full">
              <span class="detail-meta__label">Comentarios</span>
              <span class="detail-meta__value">{{ detailVoucher.comments }}</span>
            </div>
          </div>

          <h4 class="section-title">Piezas del vale</h4>
          <table class="data-table data-table--embedded">
            <thead>
              <tr>
                <th>Código</th>
                <th>Pieza</th>
                <th class="col--num">Cantidad</th>
                <th class="col--num">Costo unitario</th>
                <th class="col--num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in detailVoucher.items" :key="item.id">
                <td>{{ item.part_barcode }}</td>
                <td class="cell-strong">{{ item.part_name }}</td>
                <td class="col--num">{{ formatNumber(item.quantity) }} {{ item.part_unit_of_measure || '' }}</td>
                <td class="col--num">${{ formatNumber(item.unit_cost_snapshot) }}</td>
                <td class="col--num cell-strong">${{ formatNumber(item.subtotal) }}</td>
              </tr>
            </tbody>
          </table>
          <p class="detail-total">Total <strong>${{ formatNumber(detailVoucher.total) }}</strong></p>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="detailVoucher = null">Cerrar</button>
          <button class="action-button" @click="printTicketCurrent">Imprimir ticket</button>
          <button class="action-button action-primary" @click="printCurrent">Imprimir</button>
        </div>
      </div>
    </div>

    <!-- Create modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="vs-create-title">
        <h3 id="vs-create-title">Nuevo vale de salida</h3>
        <div class="modal-body">
          <div class="requisition-fulfill">
            <label for="vs-req-scan">Surtir requisición</label>
            <input
              id="vs-req-scan"
              ref="requisitionScan"
              type="text"
              v-model="requisitionCode"
              placeholder="Escanear código o escribir RQ-1"
              @keyup.enter="loadRequisition"
            />
            <button class="mini-button" type="button" @click="loadRequisition" :disabled="loadingRequisition">
              {{ loadingRequisition ? 'Buscando...' : 'Surtir requisición' }}
            </button>
          </div>
          <p v-if="form.requisition" class="requisition-chip">
            <span>Vinculada a <strong>{{ form.requisition_number }}</strong>. Varios vales pueden surtir la misma requisición.</span>
            <button type="button" class="mini-button mini-button--danger" @click="clearRequisitionLink">Quitar</button>
          </p>
          <div class="modal-grid">
            <div class="form-input-group">
              <label for="vs-auth">Autoriza *</label>
              <select id="vs-auth" v-model="form.authorized_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="emp.id" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="vs-recv">Recibe *</label>
              <select id="vs-recv" v-model="form.received_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="emp.id" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="vs-comments">Comentarios</label>
              <textarea id="vs-comments" v-model="form.comments" rows="2"></textarea>
            </div>
          </div>

          <div class="items-section">
            <div class="items-header">
              <h4>Piezas</h4>
              <input
                type="text"
                class="part-search"
                v-model="partSearch"
                @input="searchParts"
                placeholder="Buscar pieza por nombre o código..."
                aria-label="Buscar pieza"
              />
              <button class="mini-button" @click="addItem">Agregar pieza</button>
            </div>
            <div v-for="(item, index) in form.items" :key="index" class="item-row">
              <select v-model="item.part" aria-label="Pieza">
                <option value="">Seleccione pieza</option>
                <option v-for="part in partOptions" :key="part.id" :value="part.id">
                  {{ part.barcode }} - {{ part.name }} (stock: {{ formatNumber(part.current_stock) }})
                </option>
              </select>
              <input type="number" min="0.01" step="0.01" v-model.number="item.quantity" placeholder="Cantidad" aria-label="Cantidad" />
              <button class="mini-button mini-button--danger" aria-label="Quitar pieza" @click="form.items.splice(index, 1)">✕</button>
            </div>
            <p v-if="form.items.length === 0" class="items-empty">
              Agrega al menos una pieza para guardar el vale.
            </p>
          </div>
        </div>

        <div class="modal-actions">
          <button class="action-button" @click="closeCreateModal">Cancelar</button>
          <button class="action-button action-primary" @click="saveVoucher" :disabled="saving">
            {{ saving ? 'Guardando...' : 'Guardar vale' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from '@/utils/warehouseSwal'
import { printExitVoucher } from '@/utils/printVoucher'
import { printExitVoucherTicket } from '@/utils/printTicket'
import { normalizeScannedCode } from '@/utils/scannedCode'
import warehouseTable from '@/mixins/warehouseTable'

export default {
  name: 'WarehouseExitVouchers',
  mixins: [warehouseTable],
  data() {
    return {
      vouchers: [],
      loading: false,
      saving: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      filters: {
        voucher_number: '',
        part: '',
        authorized_by: '',
        received_by: '',
        order_number: '',
        requisition_number: '',
        start_date: '',
        end_date: '',
      },
      sortState: { key: '', dir: 'asc' },
      detailVoucher: null,
      showCreateModal: false,
      form: this.emptyForm(),
      employees: [],
      partOptions: [],
      partSearch: '',
      partSearchTimeout: null,
      requisitionCode: '',
      loadingRequisition: false,
      permissions: [],
    }
  },
  computed: {
    canCreate() {
      return this.hasPerm('maintenance.create_exit_voucher')
    },
  },
  created() {
    this.loadPermissions()
  },
  mounted() {
    this.fetchVouchers(1)
    this.fetchEmployees()
    this.fetchPartOptions('')
  },
  methods: {
    emptyForm() {
      return {
        authorized_by: '',
        received_by: '',
        comments: '',
        requisition: null,
        requisition_number: '',
        items: [{ part: '', quantity: null }],
      }
    },
    loadPermissions() {
      try {
        this.permissions = JSON.parse(localStorage.getItem('userPermissions')) || []
      } catch (error) {
        this.permissions = []
      }
    },
    hasPerm(perm) {
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`)
    },
    backMainPanel() {
      this.$router.push('/main-panel')
    },
    backWarehouse() {
      this.$router.push('/warehouse')
    },
    formatNumber(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return 'N/A'
      return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDateTime(value) {
      if (!value) return 'N/A'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'N/A'
      return date.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
    },
    toggleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.dir = this.sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortState = { key, dir: 'asc' }
      }
      this.fetchVouchers(1)
    },
    applyFilters() {
      this.fetchVouchers(1)
    },
    clearFilters() {
      this.filters = {
        voucher_number: '', part: '', authorized_by: '', received_by: '', order_number: '', requisition_number: '', start_date: '', end_date: '',
      }
      this.fetchVouchers(1)
    },
    goToPage(pageNumber) {
      if (typeof pageNumber !== 'number') return
      if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) return
      this.fetchVouchers(pageNumber)
    },
    async fetchVouchers(page) {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      try {
        let orderingParam = ''
        if (this.sortState.key) {
          orderingParam = `&ordering=${this.sortState.dir === 'desc' ? '-' : ''}${this.sortState.key}`
        }
        const response = await axios.post(
          `${apiDirectory}/warehouse/get-exit-vouchers-filtered?page=${page}&page_size=${this.pageSize}${orderingParam}`,
          this.filters,
          { headers: { 'Content-Type': 'application/json' } },
        )
        this.vouchers = response.data.results || []
        this.totalCount = response.data.count || 0
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        this.currentPage = page
      } catch (error) {
        console.error('Failed to fetch exit vouchers:', error)
        Swal.fire('Error', 'No se pudieron cargar los vales de salida.', 'error')
      } finally {
        this.loading = false
      }
    },
    async fetchEmployees() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/employees-for-forms`)
        this.employees = response.data.results || []
      } catch (error) {
        console.error('Failed to fetch employees:', error)
      }
    },
    async fetchPartOptions(search) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/parts-for-forms`, {
          params: { search },
        })
        this.partOptions = response.data.results || []
      } catch (error) {
        console.error('Failed to fetch parts:', error)
      }
    },
    searchParts() {
      clearTimeout(this.partSearchTimeout)
      this.partSearchTimeout = setTimeout(() => {
        this.fetchPartOptions(this.partSearch)
      }, 300)
    },
    openDetail(voucher) {
      this.detailVoucher = voucher
    },
    printCurrent() {
      printExitVoucher(this.detailVoucher)
    },
    printTicketCurrent() {
      printExitVoucherTicket(this.detailVoucher)
    },
    openCreateModal() {
      this.form = this.emptyForm()
      this.requisitionCode = ''
      this.showCreateModal = true
      this.$nextTick(() => {
        if (this.$refs.requisitionScan) this.$refs.requisitionScan.focus()
      })
    },
    closeCreateModal() {
      this.showCreateModal = false
    },
    addItem() {
      this.form.items.push({ part: '', quantity: null })
    },
    ensurePartOption(item) {
      if (this.partOptions.some((part) => part.id === item.part)) return
      this.partOptions.push({
        id: item.part,
        barcode: item.part_barcode,
        name: item.part_name,
        current_stock: item.current_stock,
        unit_of_measure: item.part_unit_of_measure,
      })
    },
    clearRequisitionLink() {
      this.form.requisition = null
      this.form.requisition_number = ''
    },
    linesFromRequisition(requisition, mode) {
      const pending = (requisition.items || []).filter((item) => Number(item.remaining_quantity) > 0)
      const lines = []
      pending.forEach((item) => {
        const remaining = Number(item.remaining_quantity)
        const stock = Number(item.current_stock) || 0
        const quantity = mode === 'partial' ? Math.min(stock, remaining) : remaining
        if (quantity > 0) {
          this.ensurePartOption(item)
          lines.push({ part: item.part, quantity })
        }
      })
      return lines
    },
    async loadRequisition() {
      this.requisitionCode = normalizeScannedCode(this.requisitionCode)
      const code = (this.requisitionCode || '').trim()
      if (!code) {
        Swal.fire('Falta el código', 'Escanea o escribe el folio de la requisición.', 'warning')
        return
      }
      const hasItems = this.form.items.some((item) => item.part && Number(item.quantity) > 0)
      if (hasItems || this.form.requisition) {
        const replace = await Swal.fire({
          title: 'Reemplazar piezas',
          text: 'El vale ya tiene piezas. Se sustituirán por las de la requisición.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Reemplazar',
          cancelButtonText: 'Cancelar',
        })
        if (!replace.isConfirmed) return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.loadingRequisition = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/lookup-requisition`, {
          code,
        }, { headers: { 'Content-Type': 'application/json' } })
        const requisition = response.data
        const pending = (requisition.items || []).filter((item) => Number(item.remaining_quantity) > 0)
        if (requisition.status === 'fulfilled' || pending.length === 0) {
          Swal.fire('Requisición surtida', `La requisición ${requisition.requisition_number} ya fue surtida por completo.`, 'info')
          return
        }
        const shortages = pending.filter((item) => (Number(item.current_stock) || 0) < Number(item.remaining_quantity))
        let mode = 'full'
        if (shortages.length) {
          const escapeHtml = (value) => String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
          const lines = shortages.map((item) => {
            const name = escapeHtml(item.part_name || 'Pieza')
            const barcode = escapeHtml(item.part_barcode || '')
            return `${name} (${barcode}): pendiente ${this.formatNumber(item.remaining_quantity)}, en stock ${this.formatNumber(item.current_stock)}`
          }).join('<br>')
          const decision = await Swal.fire({
            title: 'Stock insuficiente',
            html: `No hay existencia suficiente para surtir completa la requisición ${requisition.requisition_number}.<br><br>${lines}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Surtir parcial',
            cancelButtonText: 'Esperar a completar',
            allowOutsideClick: false,
            allowEscapeKey: false,
          })
          if (!decision.isConfirmed) return
          mode = 'partial'
        }
        const lines = this.linesFromRequisition(requisition, mode)
        if (lines.length === 0) {
          Swal.fire('Sin existencia', 'Ninguna pieza pendiente tiene stock. La requisición sigue abierta.', 'warning')
          return
        }
        this.form.requisition = requisition.id
        this.form.requisition_number = requisition.requisition_number
        this.form.items = lines
        this.requisitionCode = ''
        const partialNote = mode === 'partial' ? ' Se cargó solo lo disponible.' : ''
        await Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          background: '#d4edda',
          title: `Piezas de ${requisition.requisition_number}`,
          text: `Orden ${requisition.maintenance_order_number || 'sin folio'}.${partialNote}`,
          showConfirmButton: false,
          timer: 2200,
        })
      } catch (error) {
        console.error('Failed to load requisition:', error)
        const message = error.response?.data?.error || 'No se pudo identificar la requisición.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.loadingRequisition = false
      }
    },
    async saveVoucher() {
      if (!this.form.authorized_by || !this.form.received_by) {
        Swal.fire('Falta información', 'Selecciona quién autoriza y quién recibe.', 'warning')
        return
      }
      const items = this.form.items.filter((item) => item.part && Number(item.quantity) > 0)
      if (items.length === 0) {
        Swal.fire('Falta información', 'Agrega al menos una pieza con cantidad mayor a cero.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/create-exit-voucher`, {
          authorized_by: this.form.authorized_by,
          received_by: this.form.received_by,
          comments: this.form.comments || null,
          requisition: this.form.requisition || null,
          items: items.map((item) => ({ part: item.part, quantity: item.quantity })),
        }, { headers: { 'Content-Type': 'application/json' } })
        this.showCreateModal = false
        const result = await Swal.fire({
          title: 'Vale de salida creado',
          text: `Folio ${response.data.voucher_number}. ¿Deseas imprimirlo?`,
          icon: 'success',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Imprimir',
          denyButtonText: 'Imprimir ticket',
          cancelButtonText: 'Después',
        })
        if (result.isConfirmed) printExitVoucher(response.data)
        if (result.isDenied) printExitVoucherTicket(response.data)
        this.fetchVouchers(1)
        this.fetchPartOptions(this.partSearch)
      } catch (error) {
        console.error('Failed to create exit voucher:', error)
        const data = error.response?.data
        if (data?.error === 'insufficient_stock' && Array.isArray(data.details)) {
          const lines = data.details
            .map((d) => `${d.name} (${d.barcode}): disponible ${d.available}, solicitado ${d.requested}`)
            .join('<br>')
          Swal.fire({
            title: 'Stock insuficiente',
            html: lines,
            icon: 'error',
          })
        } else {
          Swal.fire('Error', data?.error || 'No se pudo crear el vale de salida.', 'error')
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped src="@/styles/warehouse.css"></style>
